import type { PushPlatform } from '@/lib/sdk-official/resources/push'

/**
 * F-186: what this browser is, for the install and notification prompts. The
 * user agent only picks wording and steps; capability checks use the APIs.
 */

/** Which steps the "blocked" and "install" texts show. */
export type BrowserKind = 'iosApp' | 'iosSafari' | 'android' | 'chromeDesktop' | 'firefox' | 'safariMac' | 'other'

const ua = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent || '')

/** iPadOS presents itself as a Mac; the touch points give it away. */
const isIpadOs = () => typeof navigator !== 'undefined' && /Macintosh/.test(ua()) && (navigator.maxTouchPoints || 0) > 1

export const detectPlatform = (): PushPlatform => {
  const agent = ua()
  if (/iPhone|iPad|iPod/.test(agent) || isIpadOs()) return 'ios'
  if (/Android/i.test(agent)) return 'android'
  if (/Windows/i.test(agent)) return 'windows'
  if (/Macintosh|Mac OS X/i.test(agent)) return 'macos'
  if (/Linux|CrOS/i.test(agent)) return 'linux'
  return 'other'
}

/** Opened from the home screen / as an installed app. */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches
  } catch {
    return false
  }
}

/** Browsers inside other apps (Instagram, Facebook, WhatsApp, LinkedIn): no push, no install. */
export const isInAppBrowser = (): boolean => /Instagram|FBAN|FBAV|FB_IAB|WhatsApp|LinkedInApp|Line\//i.test(ua())

/** The iOS version, or null off iOS / when the agent does not say. */
export const iosVersion = (): number | null => {
  const match = ua().match(/OS (\d+)[_.](\d+)/)
  if (match) return Number(match[1]) + Number(match[2]) / 100
  if (isIpadOs()) {
    const safari = ua().match(/Version\/(\d+)\.(\d+)/)
    if (safari) return Number(safari[1]) + Number(safari[2]) / 100
  }
  return null
}

const isFirefox = () => /Firefox|FxiOS/i.test(ua())
const isChromium = () => /Chrome|Chromium|CriOS|Edg|SamsungBrowser|OPR/i.test(ua())

export const browserKind = (): BrowserKind => {
  const platform = detectPlatform()
  if (platform === 'ios') return isStandalone() ? 'iosApp' : 'iosSafari'
  if (platform === 'android') return 'android'
  if (isFirefox()) return 'firefox'
  if (platform === 'macos' && !isChromium()) return 'safariMac'
  if (isChromium()) return 'chromeDesktop'
  return 'other'
}

/** The browser can receive push at all (iOS: only inside the installed app). */
export const pushApisPresent = (): boolean =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window

export const currentPermission = (): NotificationPermission | 'unsupported' =>
  typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
